import AddIcon from '@mui/icons-material/Add'
import CommentIcon from '@mui/icons-material/Comment'
import DeleteIcon from '@mui/icons-material/Delete'
import ExpandCircleDownIcon from '@mui/icons-material/ExpandCircleDown'
import RemoveIcon from '@mui/icons-material/Remove'
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight'
import {
  Box,
  Collapse,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Typography,
  alpha,
} from '@mui/material'

import type React from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ParticipantAvatar } from '../catering-team-order/participant-avatar'
import type { Item } from '../consolidated-basket'
import { ItemNotePopover } from '../consolidated-basket/item-note-popover'
import { DisabledReasonTooltip } from './disabled-reason-tooltip'

const qtyButtonSx = {
  width: 27,
  height: 27,
  border: '1.5px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
  '&:hover': { borderColor: 'primary.main' },
} as const

type Props = {
  onDeleteClick: (itemId: string) => void
  onQuantityChange: (itemId: string, quantity: number) => void
  onNoteChange: (itemId: string, note: string) => void
  showItemNotes: boolean
  isUnavailable?: boolean
  /** While the basket recalculates, the line price can briefly read £0 — show a skeleton instead, matching the totals rows. */
  priceUpdating?: boolean
} & Item

const ReorderBasketItem: React.FC<Props> = ({
  onDeleteClick,
  onQuantityChange,
  onNoteChange,
  showItemNotes,
  isUnavailable = false,
  priceUpdating = false,
  ...item
}) => {
  const { t } = useTranslation()
  const {
    id,
    itemOptions,
    price,
    name,
    note,
    minimumQuantity,
    quantity,
    updatedByVendor,
    canUpdateQty,
    canDelete,
    addedByUserId,
    addedByName,
    qtyLockedForCateringTeamOrder,
    isCustomItem,
  } = item
  const [expanded, setExpanded] = useState(false)

  // Custom items have no itemReference in basket.items, so
  // updateItemInBasket can't target them — notes are read-only. canUpdateQty
  // mirrors the backend's group-order contributor check (userCannotEditGroupOrderLine),
  // so a contributor can't open an editor the mutation would reject anyway.
  const canEditNoteInline = showItemNotes && !isCustomItem && canUpdateQty
  const hasExpandableContent = itemOptions.length > 0 || (!canEditNoteInline && note.length > 0)

  const textColor = isUnavailable || updatedByVendor ? 'warning.main' : 'text.primary'

  const qtyDisabled = !canUpdateQty
  const qtyDisabledReason = qtyLockedForCateringTeamOrder
    ? t('marketplace:cateringTeamOrderQtyDisabled')
    : undefined

  const handleDecrement = () => {
    if (qtyDisabled) return
    if (quantity - 1 < (minimumQuantity ?? 1)) return
    onQuantityChange(id, quantity - 1)
  }
  const handleIncrement = () => {
    if (qtyDisabled) return
    onQuantityChange(id, quantity + 1)
  }

  return (
    <Stack
      spacing={1}
      sx={{
        width: '100%',
        paddingY: '11px',
        bgcolor: isUnavailable ? (theme) => alpha(theme.palette.warning.main, 0.08) : 'transparent',
        borderRadius: isUnavailable ? 1 : 0,
        border: isUnavailable ? '1px solid' : 'none',
        borderColor: isUnavailable ? 'warning.main' : 'transparent',
        paddingX: isUnavailable ? 1 : 0,
        opacity: isUnavailable ? 0.85 : 1,
      }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.25}>
        <Typography
          color={textColor}
          sx={{ fontSize: 13, fontWeight: 600, flex: 1, lineHeight: 1.35 }}>
          {name}
        </Typography>
        <Typography color={textColor} sx={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>
          {priceUpdating ? <Skeleton variant="text" width={60} /> : price}
        </Typography>
      </Stack>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Stack spacing={0.25} sx={{ pl: 1 }}>
          {itemOptions?.map((option) => (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              key={`${option.name}-${option.id}`}>
              <Box display="flex" alignItems="center">
                <SubdirectoryArrowRightIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
                <Typography variant="caption" sx={{ ml: 1 }}>
                  {option.name} ×{option.qty}
                </Typography>
              </Box>
              <Typography variant="caption">{option.priceExTax}</Typography>
            </Stack>
          ))}
          {!canEditNoteInline && note && (
            <Stack direction="row" alignItems="center">
              <CommentIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
              <Typography variant="caption" sx={{ pl: 1 }}>
                {note}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Collapse>

      <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
        <DisabledReasonTooltip reason={qtyDisabledReason}>
          <IconButton
            size="small"
            onClick={handleDecrement}
            disabled={qtyDisabled}
            sx={qtyButtonSx}
            aria-label="Decrease quantity">
            <RemoveIcon sx={{ fontSize: 13 }} />
          </IconButton>
        </DisabledReasonTooltip>
        <Typography
          data-cy="basket-item-quantity"
          sx={{ fontSize: 13, fontWeight: 700, minWidth: 22, textAlign: 'center' }}>
          {quantity}
        </Typography>
        <DisabledReasonTooltip reason={qtyDisabledReason}>
          <IconButton
            size="small"
            onClick={handleIncrement}
            disabled={qtyDisabled}
            sx={qtyButtonSx}
            aria-label="Increase quantity">
            <AddIcon sx={{ fontSize: 13 }} />
          </IconButton>
        </DisabledReasonTooltip>

        {canEditNoteInline && (
          <ItemNotePopover
            note={note}
            onSave={(newNote) => onNoteChange(id, newNote)}
            onRemove={() => onNoteChange(id, '')}
          />
        )}

        {hasExpandableContent && (
          <IconButton
            size="small"
            onClick={() => setExpanded((v) => !v)}
            sx={{
              transform: expanded ? 'rotate(180deg)' : 'none',
              transition: 'transform .2s',
              ml: 0.5,
            }}
            aria-label={expanded ? 'Hide options' : 'Show options'}>
            <ExpandCircleDownIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
          </IconButton>
        )}

        {/* Spacer pushes the delete button to the far right of the row. */}
        <Box sx={{ flexGrow: 1 }} />

        {canDelete && (
          <IconButton
            size="small"
            onClick={() => onDeleteClick(id)}
            sx={{
              width: 27,
              height: 27,
              borderRadius: '6px',
              '&:hover': {
                bgcolor: (theme) => alpha(theme.palette.error.main, 0.12),
                '& svg': { color: 'error.main' },
              },
            }}
            aria-label="Remove item">
            <DeleteIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          </IconButton>
        )}
      </Stack>

      {addedByName ? (
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <ParticipantAvatar
            userId={addedByUserId}
            name={addedByName}
            sx={{ width: 18, height: 18, fontSize: 9 }}
          />
          <Typography variant="caption" color="text.secondary" noWrap title={addedByName}>
            {t('marketplace:addedBy', { name: addedByName })}
          </Typography>
        </Stack>
      ) : null}

      <Divider sx={{ pt: 0.5 }} />
    </Stack>
  )
}

export default ReorderBasketItem
